import React, { useContext, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { List } from "react-window";
import { debounce } from "lodash";
import { ChevronLeftIcon, Search } from "lucide-react";
import InnerLayout from "../../../layouts/inner-layout";
import { DataContext } from "../../../layouts/main-layout";
import TabsComponent from "../../../helper-components/tabs-component";
import ConfirmModal from "../../../helper-components/confirm-modal";
import AccessDenied from "../../../helper-components/access-denied";
import successToast from "../../../helper-components/success-toast";
import errorToast from "../../../helper-components/error-toast";
import LoadingOverlay from "../../../helper-components/overlay-loading";
import AddButton, { DraftButton } from "../../../buttons/button";
import EditIcon, { PrintIcon, VoidIcon } from "../../../../assets/icons/action-icons";
import {
  trayActiveApi,
  trayCreateApi,
  trayInactiveApi,
  trayUpdateApi,
} from "../../../../redux/apis/tray-api";
import useTrayList from "./use-tray-list";
import TrayGenerateModal from "./tray-generate-modal";
import TrayLabelPrintModal, { confirmLabelPrinterAccess } from "./tray-label-print-modal";
import TrayListRow, {
  SaveCancelButtons,
  SelectCheckbox,
  TRAY_ROW_HEIGHT,
  TrayNameInput,
  trayCellClass,
  trayGridColumns,
} from "./tray-list-row";
import { errorMessage, failureMessage } from "./tray-list-state";
import useTraySelection from "./use-tray-selection";
import TraySelectionBar from "./tray-selection-bar";

// The permission module seeded by the backend's migration_tray_permission.
const MODULE = "tray";
const ACTIVE_TAB = "Active";
const INACTIVE_TAB = "Inactive";
const TABS = [ACTIVE_TAB, INACTIVE_TAB];
const HEADINGS = ["S.NO", "TRAY NAME", "TRAY BARCODE", "ACTIONS"];
const SEARCH_DEBOUNCE_MS = 400;
// Start fetching the next page this many rows before the end of the list.
const LOAD_AHEAD_ROWS = 5;

const headClass =
  "px-[8px] text-left whitespace-nowrap text-[var(--list-table-heading-fg-color)] text-[length:--list-table-heading-font-size] font-[var(--list-table-heading-font-weight)]";

const messageClass =
  "h-[50px] flex items-center justify-center text-[var(--list-table-row-fg-color)] text-[length:--list-table-row-font-size]";

const TrayManagement = () => {
  const navigate = useNavigate();
  const { permissionFn } = useContext(DataContext);
  const permission = permissionFn(MODULE);
  // permissionFn returns {} until the permission request resolves; every user
  // has a 'tray' row once the backend seed has run, so {} means "not loaded yet".
  const permissionsLoaded = Object.keys(permission || {}).length > 0;
  const can = (action) => permission?.[action] === true || permission?.all === true;
  const canRead = can("read");

  const [currentTab, setCurrentTab] = useState(ACTIVE_TAB);
  const [searchInput, setSearchInput] = useState("");
  const [searchValue, setSearchValue] = useState("");
  const status = currentTab === ACTIVE_TAB ? "active" : "inactive";
  const { trays, totalCount, loading, hasMore, loadError, loadMore, reload, retry, replace, remove } =
    useTrayList({ status, searchValue, enabled: canRead });
  // Only active trays can be reserved, so only they get label printing.
  const selectable = currentTab === ACTIVE_TAB;
  const selection = useTraySelection({ status, searchValue, trays, totalCount });

  const [editingId, setEditingId] = useState(null);
  const [draftName, setDraftName] = useState("");
  const [adding, setAdding] = useState(false);
  const [newName, setNewName] = useState("");
  const [saving, setSaving] = useState(false);
  const [confirmTarget, setConfirmTarget] = useState(null);
  const [showGenerate, setShowGenerate] = useState(false);
  const [labelTrays, setLabelTrays] = useState([]);
  const [showLabelPrint, setShowLabelPrint] = useState(false);
  // A bulk print clears the selection once the labels are sent.
  const [labelFromSelection, setLabelFromSelection] = useState(false);
  const [checkingPrinterAccess, setCheckingPrinterAccess] = useState(false);

  const applySearch = useMemo(
    () => debounce((value) => setSearchValue(value.trim()), SEARCH_DEBOUNCE_MS),
    []
  );
  useEffect(() => () => applySearch.cancel(), [applySearch]);

  // A new tab or search loads a different list, so any half-finished rename goes.
  useEffect(() => {
    setEditingId(null);
  }, [status, searchValue]);

  const cancelEdit = () => {
    setEditingId(null);
    setDraftName("");
  };

  const startEdit = (tray) => {
    setAdding(false);
    setEditingId(tray.tray_id);
    setDraftName(tray.tray_name ?? "");
  };

  const cancelAdd = () => {
    setAdding(false);
    setNewName("");
  };

  // New trays are active, so adding always happens on the Active tab.
  const startAdd = () => {
    cancelEdit();
    setNewName("");
    setAdding(true);
    setCurrentTab(ACTIVE_TAB);
  };

  // Shared by rename and create: checks the name, calls the API, toasts the outcome.
  const submitName = (request, name, onSaved) => {
    const trimmed = name.trim();
    if (!trimmed) {
      errorToast("Tray name is required");
      return;
    }
    setSaving(true);
    request(trimmed)
      .then((res) => {
        const failed = failureMessage(res);
        if (failed) {
          errorToast(failed);
          return;
        }
        successToast(res?.data?.message ?? "Tray saved");
        onSaved(res?.data?.data);
      })
      .catch((err) => errorToast(errorMessage(err, "Failed to save the tray")))
      .finally(() => setSaving(false));
  };

  const saveEdit = () => {
    const tray = trays.find((item) => item.tray_id === editingId);
    if (tray && draftName.trim() === tray.tray_name) {
      cancelEdit();
      return;
    }
    submitName(
      (tray_name) => trayUpdateApi({ tray_id: editingId, tray_name }),
      draftName,
      (saved) => {
        if (saved) {
          replace(saved);
          selection.refresh(saved);
        }
        cancelEdit();
      }
    );
  };

  // The list is newest first, so a reload shows the new tray at the top.
  const saveNew = () =>
    submitName((tray_name) => trayCreateApi({ tray_name }), newName, () => {
      cancelAdd();
      reload();
    });

  // Thermal label print, as for GRN: the browser has to let the page reach the
  // printer app first, then the print modal opens for these trays.
  const openLabelPrint = (traysToPrint, { fromSelection = false } = {}) => {
    if (!traysToPrint?.length) return;
    setCheckingPrinterAccess(true);
    confirmLabelPrinterAccess()
      .then((allowed) => {
        if (!allowed) return;
        setLabelTrays(traysToPrint);
        setLabelFromSelection(fromSelection);
        setShowLabelPrint(true);
      })
      .finally(() => setCheckingPrinterAccess(false));
  };

  // Generated trays are active and the list is newest first, so they show at the
  // top of the Active tab (switching tabs reloads on its own). New trays need
  // labels before they can be scanned, so their label print opens straight away.
  const handleGenerated = (generated) => {
    setShowGenerate(false);
    cancelAdd();
    if (currentTab === ACTIVE_TAB) reload();
    else setCurrentTab(ACTIVE_TAB);
    openLabelPrint(generated);
  };

  const applyStatusChange = () => {
    const tray = confirmTarget;
    setConfirmTarget(null);
    if (!tray) return;
    const request = tray.is_active === false ? trayActiveApi : trayInactiveApi;
    request(tray.tray_id)
      .then((res) => {
        const failed = failureMessage(res);
        if (failed) {
          errorToast(failed);
          return;
        }
        successToast(res?.data?.message ?? "Tray updated");
        if (tray.tray_id === editingId) cancelEdit();
        // It now belongs to the other tab (and inactive trays get no labels).
        remove(tray.tray_id);
        selection.drop(tray.tray_id);
      })
      .catch((err) => errorToast(errorMessage(err, "Failed to update the tray status")));
  };

  // Inactivating is the soft delete (needs delete); reactivating needs update.
  const menuItemsFor = (tray) => {
    const isActive = tray.is_active !== false;
    return [
      isActive && {
        label: "Print Label",
        icon: <PrintIcon fill="var(--popover-fg-color)" />,
        onClick: () => openLabelPrint([tray]),
      },
      can("update") && {
        label: "Edit",
        icon: <EditIcon fill="var(--popover-fg-color)" />,
        onClick: () => startEdit(tray),
      },
      (isActive ? can("delete") : can("update")) && {
        label: isActive ? "Inactive" : "Activate",
        icon: <VoidIcon fill="var(--popover-fg-color)" />,
        onClick: () => setConfirmTarget(tray),
      },
    ].filter(Boolean);
  };

  const handleRowsRendered = (_visibleRows, allRows) => {
    if (allRows.stopIndex >= trays.length - LOAD_AHEAD_ROWS) loadMore();
  };

  if (!canRead) {
    return (
      <InnerLayout title="Settings">
        <AccessDenied
          message={
            permissionsLoaded ? "You don't have permission to view Tray Management." : "Loading..."
          }
        />
      </InnerLayout>
    );
  }

  const emptyText = searchValue
    ? "No trays match that search."
    : currentTab === ACTIVE_TAB
    ? "No active trays."
    : "No inactive trays.";
  const confirmingActivate = confirmTarget?.is_active === false;

  return (
    <InnerLayout title="Settings" list={true}>
      <div className="flex flex-col gap-3 h-full px-6 pt-4 pb-4">
        <div className="flex justify-between items-center gap-3 flex-wrap">
          <div className="flex items-center gap-2 text-sml">
            <button className="flex items-center gap-2" onClick={() => navigate("/settings")}>
              <ChevronLeftIcon className="h-5 w-5 inline-block" />
              Data Setup Settings /
            </button>
            <span>Tray Management</span>
          </div>
          <div className="flex items-center gap-3">
            <div
              style={{ minWidth: "18rem", height: "2.5rem" }}
              className="border border-[var(--search-input-border)] flex justify-between items-center rounded-md"
            >
              <input
                placeholder="Search by name or barcode"
                className="table_search_input px-3 py-2 !border-none !outline-none !ring-0 text-[length:var(--search-label-font-size)] font-[var(--search-place-font-weight)]"
                type="text"
                value={searchInput}
                onChange={(event) => {
                  setSearchInput(event.target.value);
                  applySearch(event.target.value);
                }}
              />
              <span className="px-2">
                <Search size="16" color="#9CA3AF" />
              </span>
            </div>
            {selectable && (
              <DraftButton
                text={selection.count ? `Print Labels (${selection.count})` : "Print Labels"}
                isDisabled={selection.count === 0 || selection.selectingAll}
                onclkFn={() => openLabelPrint(selection.toPrint(), { fromSelection: true })}
                className="text-primary border border-primary border-solid px-6 py-2 items-center rounded-md flex justify-center fw_500 text-sm whitespace-nowrap disabled:opacity-50 disabled:cursor-not-allowed"
              />
            )}
            {can("create") && (
              <>
                <DraftButton
                  text="Generate Trays"
                  onclkFn={() => setShowGenerate(true)}
                  className="text-primary border border-primary border-solid px-6 py-2 items-center rounded-md flex justify-center fw_500 text-sm whitespace-nowrap"
                />
                <AddButton text="Add Tray" onclkFn={startAdd} prefixIcon={true} />
              </>
            )}
          </div>
        </div>

        <TabsComponent
          itemTabs={TABS}
          currentTab={currentTab}
          setCurrentTab={setCurrentTab}
          handleClickfn={(tab) => {
            cancelAdd();
            setCurrentTab(tab);
          }}
        />

        {selectable && selection.count > 0 && (
          <TraySelectionBar
            count={selection.count}
            totalCount={totalCount}
            isSearching={Boolean(searchValue)}
            allMatchingSelected={selection.allMatchingSelected}
            selectingAll={selection.selectingAll}
            onSelectAll={selection.selectAll}
            onClear={selection.clear}
          />
        )}

        <div className="flex-1 min-h-0 flex flex-col outline outline-[var(--list-table-border-color)] outline-1 border-radius overflow-hidden">
          <div
            role="row"
            style={{ gridTemplateColumns: trayGridColumns(selectable) }}
            className="grid items-center h-[50px] shrink-0 bg-[var(--list-table-heading-bg)] border-b border-[var(--list-table-border-color)]"
          >
            {selectable && (
              <div role="columnheader" className={headClass}>
                <SelectCheckbox
                  label="Select all trays shown"
                  checked={selection.loadedState === "all"}
                  indeterminate={selection.loadedState === "some"}
                  onChange={selection.toggleLoaded}
                />
              </div>
            )}
            {HEADINGS.map((heading) => (
              <div key={heading} role="columnheader" className={headClass}>
                {heading}
              </div>
            ))}
          </div>

          {adding && (
            <div
              role="row"
              style={{ gridTemplateColumns: trayGridColumns(selectable), height: TRAY_ROW_HEIGHT }}
              className="grid items-center shrink-0 border-b border-[var(--list-table-border-color)]"
            >
              {selectable && <div />}
              <div className={trayCellClass}>--</div>
              <div className={trayCellClass}>
                <TrayNameInput
                  value={newName}
                  onChange={setNewName}
                  onSave={saveNew}
                  onCancel={cancelAdd}
                  saving={saving}
                />
              </div>
              <div className={trayCellClass}>Generated on save</div>
              <div className={trayCellClass}>
                <SaveCancelButtons onSave={saveNew} onCancel={cancelAdd} saving={saving} />
              </div>
            </div>
          )}

          <div className="flex-1 min-h-0">
            {trays.length === 0 && loadError ? (
              <div className={`${messageClass} gap-2`}>
                Couldn't load trays.
                <button type="button" className="text-primary underline" onClick={retry}>
                  Retry
                </button>
              </div>
            ) : trays.length === 0 ? (
              <div className={messageClass}>{loading ? "Loading..." : emptyText}</div>
            ) : (
              <List
                rowComponent={TrayListRow}
                rowCount={trays.length + (hasMore ? 1 : 0)}
                rowHeight={TRAY_ROW_HEIGHT}
                rowProps={{
                  trays,
                  editingId,
                  draftName,
                  setDraftName,
                  saving,
                  onSaveEdit: saveEdit,
                  onCancelEdit: cancelEdit,
                  menuItemsFor,
                  loadError,
                  onRetry: retry,
                  selectable,
                  selected: selection.selected,
                  onToggleSelect: selection.toggle,
                }}
                onRowsRendered={handleRowsRendered}
                overscanCount={5}
                style={{ height: "100%", width: "100%" }}
              />
            )}
          </div>
        </div>

        {totalCount > 0 && (
          <div className="text-xs text-[var(--list-table-row-fg-color)]">
            Showing {trays.length} of {totalCount} trays
          </div>
        )}
      </div>

      <ConfirmModal
        isOpen={Boolean(confirmTarget)}
        title={confirmingActivate ? "Activate tray?" : "Mark tray inactive?"}
        message={
          confirmingActivate
            ? `${confirmTarget?.tray_name} (${confirmTarget?.barcode}) can be reserved for drop lists and pick lists again.`
            : `${confirmTarget?.tray_name} (${confirmTarget?.barcode}) can no longer be reserved for drop lists or pick lists.`
        }
        confirmText={confirmingActivate ? "Activate" : "Inactive"}
        cancelText="Cancel"
        onConfirm={applyStatusChange}
        onCancel={() => setConfirmTarget(null)}
      />

      <TrayGenerateModal
        isOpen={showGenerate}
        onClose={() => setShowGenerate(false)}
        onGenerated={handleGenerated}
      />

      <LoadingOverlay isLoading={checkingPrinterAccess} />
      <TrayLabelPrintModal
        isOpen={showLabelPrint}
        onClose={() => setShowLabelPrint(false)}
        onPrinted={() => {
          if (labelFromSelection) selection.clear();
        }}
        trays={labelTrays}
      />
    </InnerLayout>
  );
};

export default TrayManagement;
